import React, { useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { navigate } from 'vike/client/router';
import { Binoculars } from '@phosphor-icons/react';
import { useFollowedCompanies } from '../../hooks/useFollowedCompanies';
import { useCompanyFollowActions } from '../../hooks/useCompanyFollowActions';
import { useToast } from '../../contexts/ToastContext';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useAlertUpsellToast } from '../../hooks/useAlertUpsellToast';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { maybeShowAppOfferToast } from '../../utils/app-download/app-offer';
import { localizeRoute } from '../../utils/constants/url-constants';
import { trackEvent } from '../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const promptMessages = defineMessages({
  offer: {
    id: 'company.follow.emailPrompt.offer',
    defaultMessage: 'Following {company}. Want an email when they post new roles?',
    description: 'Toast offering to turn on email updates right after following a company',
  },
  turnOn: {
    id: 'company.follow.emailPrompt.turnOn',
    defaultMessage: 'Turn on',
    description: 'Toast action that enables email updates for followed companies',
  },
  enabled: {
    id: 'company.follow.emailPrompt.enabled',
    defaultMessage: "You're set. New roles from companies you follow will be in your job-alert emails.",
    description: 'Toast confirming email updates were enabled from the follow prompt',
  },
  pendingConfirmation: {
    id: 'company.follow.emailPrompt.pendingConfirmation',
    defaultMessage: 'Almost there: confirm your email in your inbox to activate updates.',
    description: 'Toast when enabling email updates still needs the account email confirmed',
  },
  failed: {
    id: 'company.follow.emailPrompt.failed',
    defaultMessage: "Couldn't turn on email updates. You can enable them any time from the Alerts page.",
    description: 'Toast when enabling email updates from the follow prompt fails',
  },
  undo: {
    id: 'company.follow.undo',
    defaultMessage: 'Undo',
    description: 'Toast action that re-follows the company after accidentally unfollowing it',
  },
  createAccount: {
    id: 'company.follow.createAccount',
    defaultMessage: 'Activate account',
    description: 'Toast action offering guests an account so a company they just followed isn’t lost',
  },
});

export interface CompanyFollowButtonProps {
  companyId: string;
  companyName?: string;
  /** Use the light "on-brand" treatment when placed over a colored BrandPanel. */
  onBrand?: boolean;
  /** Icon-only circular button, for tight spaces like a listing row/card corner. */
  compact?: boolean;
  /** Compact button diameter in px (and a proportionally-scaled icon) — default 32, matching the
   *  SRP hit-card corner badge. Pass 44 to match job-detail's other round action buttons
   *  (JD_ICON_BTN_BASE in pages/job/@jobId/@seoSlug/utils/job-detail-action-chrome.ts). */
  compactSize?: number;
  /**
   * Non-compact "on-panel" treatment for the employers-hub brand-colour card: a filled pill in
   * the card's own derived accent/accent-ink pair (see utils/brand-color/adjust-brand-color.ts
   * and EmployerBrandCard), rather than the fixed onBrand white/theme classes: those don't fit
   * every brand hue's contrast pair the way the card's own computed accent does. Takes
   * precedence over `onBrand` when set.
   */
  panelColors?: { accent: string; accentInk: string; on: string };
  /**
   * Small labelled pill tinted from `currentColor`, for the job page brand rail's chip row: it
   * inherits the panel's measured on-color (so it stays legible on light brand panels, unlike
   * `onBrand`'s fixed white) and matches the rail's Employer Score chip. Takes precedence over
   * `compact`/`onBrand`.
   */
  chip?: boolean;
  /** Where this button is placed (job-page, srp-hit, company-profile, ...) — recorded on the analytics event. */
  surface?: string;
  /** Called after a click, before the follow/unfollow request resolves; use to stop the click reaching a wrapping Link/card. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called once a follow (true) or unfollow (false) from this button has succeeded. */
  onFollowChange?: (following: boolean) => void;
}

/**
 * Follow/unfollow entry point for the Watchlist (SEEKER_HUB_PLAN.md Phase 4). A followed
 * company is a live-data subscription (open roles, employer score, trend), not a bookmark.
 */
export const CompanyFollowButton: React.FC<CompanyFollowButtonProps> = ({
  companyId,
  companyName,
  onBrand = false,
  compact = false,
  compactSize = 32,
  panelColors,
  chip = false,
  surface,
  onClick,
  onFollowChange,
}) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { showToast } = useToast();
  const auth = useAuthOptional();
  const followedCompanies = useFollowedCompanies();
  const { followCompany, unfollowCompany } = useCompanyFollowActions();
  const { maybeOffer } = useAlertUpsellToast({ entityType: 'company', entityId: companyId, surface });
  const [busy, setBusy] = useState(false);

  const isFollowing = useMemo(
    () => followedCompanies.some((c) => c.companyId === companyId),
    [followedCompanies, companyId],
  );

  /**
   * The activation wedge: right after a first follow, signed-in users without active alert
   * emails are offered them once every 14 days: a follow is only a live subscription if its
   * updates can actually reach the user (useAlertUpsellToast, shared with RoleFollowButton).
   * Returns true when the offer toast was shown (it doubles as the follow confirmation, so the
   * caller must skip the standard toast).
   */
  const maybeOfferEmailUpdates = (): Promise<boolean> =>
    maybeOffer({
      offer: formatMessage(promptMessages.offer, { company: companyName ?? companyId }),
      turnOn: formatMessage(promptMessages.turnOn),
      enabled: formatMessage(promptMessages.enabled),
      pendingConfirmation: formatMessage(promptMessages.pendingConfirmation),
      failed: formatMessage(promptMessages.failed),
    });

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setBusy(true);
    try {
      if (isFollowing) {
        trackEvent({
          name: 'company_unfollow',
          params: {
            company_id: companyId,
            surface: surface ?? 'unknown',
            locale: intl.locale,
            logged_in: Boolean(auth?.isAuthenticated),
          },
        });
        const ok = await unfollowCompany(companyId);
        if (ok) {
          onFollowChange?.(false);
          showToast(
            formatMessage({
              id: 'company.follow.unfollowedToast',
              defaultMessage: 'Unfollowed. You won’t be notified of their new roles',
              description: 'Toast after unfollowing a company',
            }),
            'info',
            undefined,
            {
              label: formatMessage(promptMessages.undo),
              onClick: () => void followCompany(companyId, { companyName }),
            },
          );
        }
      } else {
        trackEvent({
          name: 'company_follow',
          params: {
            company_id: companyId,
            surface: surface ?? 'unknown',
            locale: intl.locale,
            logged_in: Boolean(auth?.isAuthenticated),
          },
        });
        const ok = await followCompany(companyId, { companyName });
        if (ok) {
          onFollowChange?.(true);
          const offered =
            (await maybeOfferEmailUpdates()) ||
            maybeShowAppOfferToast({
              kind: 'follow',
              isAuthenticated: Boolean(auth?.isAuthenticated),
              formatMessage,
              showToast,
              locale: intl.locale,
            });
          if (!offered) {
            const locale = normalizeSupportedLocale(intl.locale);
            // Guests follow locally (IndexedDB) with nothing syncing it anywhere else: pushing
            // "View watchlist" at someone who could lose this the moment they clear site data
            // is less useful than the actual fix: an account. Signed-in users already have that,
            // so they get the watchlist link instead.
            showToast(
              formatMessage({
                id: 'company.follow.followedToast',
                defaultMessage: 'Following. Watchlist updated',
                description: 'Toast after following a company',
              }),
              'success',
              3000,
              auth?.isAuthenticated
                ? {
                    label: formatMessage({
                      id: 'company.follow.viewWatchlist',
                      defaultMessage: 'View →',
                      description: 'Toast action link into the Watchlist',
                    }),
                    onClick: () => navigate(`/${locale}${localizeRoute('/tools/watchlist', locale)}`),
                  }
                : {
                    label: formatMessage(promptMessages.createAccount),
                    onClick: () => auth?.signIn(undefined, { screenHint: 'signup' }),
                  },
            );
          }
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const baseClass = onBrand
    ? // Inherits the brand surface's measured on-colour, so it reads on light and dark bands alike.
      'border-current/40 bg-current/10 hover:bg-current/20'
    : 'border-jc-border bg-jc-surface text-jc-ink-secondary hover:bg-jc-elevated hover:text-jc-ink';
  const activeClass = onBrand
    ? // The logo-surface pair, not the theme-reactive jc-ink: this pill sits on the brand-colour
      // panel regardless of site theme, and jc-ink flips to near-white in dark mode, which would
      // go invisible on the white active background. The pair never flips.
      'border-jc-logo-surface bg-jc-logo-surface text-jc-on-logo-surface hover:bg-jc-logo-surface/90'
    : 'border-jc-accent bg-jc-accent-muted text-jc-accent hover:bg-jc-accent-muted/70';

  const followLabel = formatMessage({
    id: 'company.follow.cta',
    defaultMessage: 'Follow',
    description: 'Follow button label to start following a company',
  });
  const followingLabel = formatMessage({
    id: 'company.follow.following',
    defaultMessage: 'Following',
    description: 'Follow button label when already following a company',
  });

  if (chip) {
    // Unfollowed: the rail's translucent currentColor chip. Followed: solid currentColor, the
    // same inversion the score chip's grade disc uses.
    const style: React.CSSProperties = isFollowing
      ? { borderColor: 'currentColor', background: 'currentColor' }
      : {
          borderColor: 'color-mix(in srgb, currentColor 30%, transparent)',
          background: 'color-mix(in srgb, currentColor 10%, transparent)',
        };
    const inner = isFollowing ? 'var(--rail-panel, var(--jc-on-logo-surface))' : undefined;
    // The pill is ~24px tall; an invisible ::after widens the tap area to about 40px, since phone
    // visitors kept tapping just beside it on search cards (2026-11-02 replays).
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-pressed={isFollowing}
        style={style}
        className="relative inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full border py-0.5 pl-1.5 pr-2.5 text-xs font-semibold leading-5 transition-[filter] after:absolute after:-inset-2 after:content-[''] hover:brightness-110 disabled:opacity-60"
      >
        <Binoculars
          className="h-3.5 w-3.5"
          weight={isFollowing ? 'fill' : 'bold'}
          style={{ color: inner }}
          aria-hidden
        />
        <span style={{ color: inner }}>{isFollowing ? followingLabel : followLabel}</span>
      </button>
    );
  }

  if (compact) {
    const iconSize = Math.round(compactSize * 0.5);
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-label={isFollowing ? followingLabel : followLabel}
        aria-pressed={isFollowing}
        title={isFollowing ? followingLabel : followLabel}
        className={`relative inline-flex flex-shrink-0 items-center justify-center rounded-full border transition-colors after:absolute after:-inset-1.5 after:content-[''] disabled:opacity-60 ${isFollowing ? activeClass : baseClass}`}
        style={{ width: compactSize, height: compactSize }}
      >
        <Binoculars style={{ width: iconSize, height: iconSize }} weight={isFollowing ? 'fill' : 'bold'} aria-hidden />
      </button>
    );
  }

  if (panelColors) {
    // The employers-hub brand-colour card: a filled accent pill ("Follow") that flips to a
    // 14%-on-colour fill ("Following ✓") once followed: the card's own computed accent/on
    // pair (adjustBrandColor), not a fixed white/theme treatment, since a brand hue can land
    // anywhere in the panel's contrast space.
    const style: React.CSSProperties = isFollowing
      ? { background: `color-mix(in srgb, ${panelColors.on} 14%, transparent)`, color: panelColors.on }
      : { background: panelColors.accent, color: panelColors.accentInk };
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-pressed={isFollowing}
        style={style}
        className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-opacity disabled:opacity-60"
      >
        {isFollowing ? (
          <Msg
            id="company.follow.followingCheck"
            defaultMessage="Following ✓"
            description="On-panel follow button label when already following a company, with a checkmark"
          />
        ) : (
          <Msg
            id="company.follow.cta"
            defaultMessage="Follow"
            description="Follow button label to start following a company"
          />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${isFollowing ? activeClass : baseClass}`}
    >
      <Binoculars className="w-4 h-4" weight={isFollowing ? 'fill' : 'bold'} aria-hidden />
      {isFollowing ? (
        <Msg
          id="company.follow.following"
          defaultMessage="Following"
          description="Follow button label when already following a company"
        />
      ) : (
        <Msg
          id="company.follow.cta"
          defaultMessage="Follow"
          description="Follow button label to start following a company"
        />
      )}
    </button>
  );
};
